import { client } from './apollo/client';
import React, { useReducer, useEffect, useState } from "react";
import Context from "../helper/context";
import { subscribeSession, refreshSession, logoutSession, currentAccount } from './auth/session';
import { reportApiError } from './errors/api-error';

import * as authActions from "./actions/authActions";
import * as authReducer from "./reducers/authReducer";

import * as customerActions from "./actions/customer";
import * as customerReducer from "./reducers/customer";

import * as unitThermokingActions from "./actions/unitThermoking";
import * as unitThermokingReducer from "./reducers/unitThermoking";

import * as maintenancePlanActions from "./actions/maintenancePlan";
import * as maintenancePlanReducer from "./reducers/maintenancePlan";

import * as supplierActions from "./actions/supplier";
import * as supplierReducer from "./reducers/supplier";

import * as productActions from "./actions/product";
import * as productReducer from "./reducers/product";

import * as productProfileActions from "./actions/productProfile";
import * as productProfileReducer from "./reducers/productProfile";

import * as trailerActions from "./actions/trailer";
import * as trailerReducer from "./reducers/trailer";

import * as shipperActions from "./actions/shipper";
import * as shipperReducer from "./reducers/shipper";

import * as driverActions from "./actions/driver";
import * as driverReducer from "./reducers/driver";

import * as simCardActions from "./actions/simCard";
import * as simCardReducer from "./reducers/simCard";

import * as userActions from "./actions/user";
import * as userReducer from "./reducers/user";

import { getAllCustomers } from "./services/customer";
import { getAllSuppliers } from "./services/supplier";
import { getAllProducts } from "./services/product";
import { getAllProductsProfile } from "./services/productProfile";
import { getAllTrailers } from "./services/trailer";
import { getAllShippers } from "./services/shipper";
import { getAllDrivers } from "./services/driver";
import { getAllSimCards } from "./services/simCard";
import { getAllUnitsThermoking } from "./services/unitThermoking";
import { getAllMaintenancePlans } from "./services/maintenancePlan";
import { getAllUsers, getUser } from "./services/user";

const ContextState = ({ children }) => {
  const [authLoading, setAuthLoading] = useState(true);
  useEffect(() => {
    let active = true;
    const unsubscribe = subscribeSession(account => {
      if (!active) return;
      if (!account) {
        [dispatchCustomer, dispatchUnitThermoking, dispatchMaintenancePlan, dispatchSupplier, dispatchProduct, dispatchProductProfile, dispatchTrailer, dispatchShipper, dispatchDriver, dispatchSimCard, dispatchUser].forEach(dispatch => dispatch({ type: 'SESSION_RESET' }));
        client.clearStore().catch(() => undefined);
      }
      dispatchAuthReducer(account ? authActions.login({ ...account, token: account.accessToken }) : authActions.logout());
    });
    refreshSession().catch(error => {
      if (error.status !== 401 && active) reportApiError(error);
    }).finally(() => { if (active) setAuthLoading(false); });
    return () => { active = false; unsubscribe(); };
  }, []);

  const [stateAuthReducer, dispatchAuthReducer] = useReducer(
    authReducer.AuthReducer,
    { 
      ...authReducer.initialState,
      isAuth: false,
      token: "",
      name: "",
    }
  );

  const [stateUnitThermoking, dispatchUnitThermoking] = useReducer(
    unitThermokingReducer.UnitThermokingReducer,
    {
      ...unitThermokingReducer.INITIAL_STATE,
    }
  )

  const [stateMaintenancePlan, dispatchMaintenancePlan] = useReducer(
    maintenancePlanReducer.MaintenancePlanReducer,
    {
      ...maintenancePlanReducer.INITIAL_STATE,
    }
  )

  const [stateCostumer, dispatchCustomer] = useReducer(
    customerReducer.CustomerReducer,
    {
      ...customerReducer.INITIAL_STATE,
    }
  )

  const [stateSupplier, dispatchSupplier] = useReducer(
    supplierReducer.SupplierReducer,
    {
      ...supplierReducer.INITIAL_STATE,
    }
  )

  const [stateProduct, dispatchProduct] = useReducer(
    productReducer.ProductReducer,
    {
      ...productReducer.INITIAL_STATE,
    }
  )

  const [stateProductProfile, dispatchProductProfile] = useReducer(
    productProfileReducer.ProductProfileReducer,
    {
      ...productProfileReducer.INITIAL_STATE,
    }
  )

  const [stateTrailer, dispatchTrailer] = useReducer(
    trailerReducer.TrailerReducer,
    {
      ...trailerReducer.INITIAL_STATE,
    }
  )

  const [stateShipper, dispatchShipper] = useReducer(
    shipperReducer.ShipperReducer,
    {
      ...shipperReducer.INITIAL_STATE,
    }
  )

  const [stateDriver, dispatchDriver] = useReducer(
    driverReducer.DriverReducer,
    {
      ...driverReducer.INITIAL_STATE,
    }
  )

  const [stateSimCard, dispatchSimCard] = useReducer(
    simCardReducer.SimCardReducer,
    {
      ...simCardReducer.INITIAL_STATE,
    }
  )

  const [stateUser, dispatchUser] = useReducer(
    userReducer.UserReducer,
    {
      ...userReducer.INITIAL_STATE,
    }
  )

  const handleLogin = () => {
    const account = currentAccount();
    if (account) dispatchAuthReducer(authActions.login({ ...account, token: account.accessToken }));
  };
  const handleLogout = async () => { await logoutSession(); };

  const handleCustomers = async () => {
    try {
    const requestedBy = currentAccount()?.id;
    let data = await getAllCustomers();
    if (requestedBy !== currentAccount()?.id) return;
    dispatchCustomer(customerActions.getAllCustomers(data));
    } catch (error) { reportApiError(error); }
  }

  const handleUnitsThermoking = async (status) => {
    try {
    const requestedBy = currentAccount()?.id;
    let data = await getAllUnitsThermoking(status);
    if (requestedBy !== currentAccount()?.id) return;
    dispatchUnitThermoking(unitThermokingActions.getAllUnitsThermoking(data));
    } catch (error) { reportApiError(error); }
  }

  const handleMaintenancePlans = async () => {
    try {
    const requestedBy = currentAccount()?.id;
    let data = await getAllMaintenancePlans();
    if (requestedBy !== currentAccount()?.id) return;
    dispatchMaintenancePlan(maintenancePlanActions.getAllMaintenancePlans(data));
    } catch (error) { reportApiError(error); }
  }

  const handleSuppliers = async () => {
    try {
    const requestedBy = currentAccount()?.id;
    let data = await getAllSuppliers();
    if (requestedBy !== currentAccount()?.id) return;
    dispatchSupplier(supplierActions.getAllSuppliers(data));
    } catch (error) { reportApiError(error); }
  }

  const handleShippers = async () => {
    try {
    const requestedBy = currentAccount()?.id;
    let data = await getAllShippers();
    if (requestedBy !== currentAccount()?.id) return;
    dispatchShipper(shipperActions.getAllShippers(data));
    } catch (error) { reportApiError(error); }
  }

  const handleProducts = async () => {
    try {
    const requestedBy = currentAccount()?.id;
    let data = await getAllProducts();
    if (requestedBy !== currentAccount()?.id) return;
    dispatchProduct(productActions.getAllProducts(data));
    } catch (error) { reportApiError(error); }
  }

  const handleProductsProfile = async () => {
    try {
    const requestedBy = currentAccount()?.id;
    let data = await getAllProductsProfile();
    if (requestedBy !== currentAccount()?.id) return;
    dispatchProductProfile(productProfileActions.getAllProductsProfile(data));
    } catch (error) { reportApiError(error); }
  }

  const handleTrailers = async (status) => {
    try {
    const requestedBy = currentAccount()?.id;
    let data = await getAllTrailers(status);
    if (requestedBy !== currentAccount()?.id) return;
    dispatchTrailer(trailerActions.getAllTrailers(data));
    } catch (error) { reportApiError(error); }
  }

  const handleDrivers = async () => {
    try {
    const requestedBy = currentAccount()?.id;
    let data = await getAllDrivers();
    if (requestedBy !== currentAccount()?.id) return;
    dispatchDriver(driverActions.getAllDrivers(data));
    } catch (error) { reportApiError(error); }
  }

  const handleSimCards = async (unityId) => {
    try {
    const requestedBy = currentAccount()?.id;
    let data = await getAllSimCards(unityId);
    if (requestedBy !== currentAccount()?.id) return;
    dispatchSimCard(simCardActions.getAllSimCards(data));
    } catch (error) { reportApiError(error); }
  }

  const handleUsers = async () => {
    try {
    const requestedBy = currentAccount()?.id;
    let data = await getAllUsers();
    if (requestedBy !== currentAccount()?.id) return;
    dispatchUser(userActions.getAllUsers(data));
    } catch (error) { reportApiError(error); }
  }

  const handleUser = async (id) => {
    let data = await getUser(id); 
    dispatchAuthReducer(authActions.setUserData(data));
  }

  return (
    <Context.Provider
      value={{
        authState: stateAuthReducer.isAuth,
        authLoading,
        userName: stateAuthReducer.name,
        tokenState: stateAuthReducer.token,
        handleUserLogin: (username) => handleLogin(username),
        handleUserLogout: () => handleLogout(),

        customers: stateCostumer.customers,
        handleGetCustomers: () => handleCustomers(),

        unitsThermoking: stateUnitThermoking.unitsThermoking,
        handleGetUnitsThermoking: (status = [0, 1]) => handleUnitsThermoking(status),

        maintenancePlans: stateMaintenancePlan.maintenancePlans,
        handleGetMaintenancePlans: () => handleMaintenancePlans(),

        suppliers: stateSupplier.suppliers,
        handleGetSuppliers: () => handleSuppliers(),

        shippers: stateShipper.shippers,
        handleGetShippers: () => handleShippers(),

        products: stateProduct.products,
        handleGetProducts: () => handleProducts(),

        productsProfile: stateProductProfile.productsProfile,
        handleGetProductsProfile: () => handleProductsProfile(),

        trailers: stateTrailer.trailers,
        handleGetTrailers: (status = [0, 1]) => handleTrailers(status),

        drivers: stateDriver.drivers,
        handleGetDrivers: () => handleDrivers(),

        simCards: stateSimCard.simCards,
        handleGetSimCards: (unityId) => handleSimCards(unityId),

        users: stateUser.users,
        handleGetUsers: () => handleUsers(),

        user: stateUser.user,
        handleGetUser: () => handleUser(),
      }}
    > 
      {children}
    </Context.Provider>
  );
};

export default ContextState;
